import React, {useContext, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import Popup from "../../../../../components/WMS/Mobile/Popup";
import {FormattedMessage} from "react-intl";
import CustomButton from "../../../../../components/CustomButton";
import { ReactComponent as PinIcon} from "assets/img/wms/pin.svg";
import {ShipmentMessageSeverity} from "../../../../../utils/ShipmentMessageType";
import {subscribeToWaybillPrint} from "../../utils/waybillPrintSubscription";
import {getShipmentById} from "../../../../../api/shipment/getWithId";
import {Grid} from "@mui/material";
import {red500} from "../../../../../assets/jss/main";
import {Link} from "react-router-dom";
import MultiExpForceStopModal from "./MultiExpForceStopModal";
import {PackingContext} from "../PackingOrderList/PackingContext";
import Badge from "../../../../../components/Badge";

ClickAndCollectPackingModal.propTypes = {
    open: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    handlePrintDocuments: PropTypes.func.isRequired,
    packingStep: PropTypes.object.isRequired,
    isLoading: PropTypes.bool.isRequired,
    setIsLoading: PropTypes.func.isRequired,
    validPrint: PropTypes.func.isRequired
};

function ClickAndCollectPackingModal({open, handleClose, handlePrintDocuments, packingStep, isLoading, setIsLoading, validPrint}) {
    const slipIsPrintedTimeout = useRef()

    const [errorMessage, setErrorMessage] = useState()
    const [stopMultiExpSession, setStopMultiExpSession] = useState(false)

    const {isMultiSessionPacking, nextShipment} = useContext(PackingContext)

    const printValidated = useRef(false)
    const handleShipmentMessageRef = useRef()
    handleShipmentMessageRef.current = handleShipmentMessage

    useEffect(() => {
        const unsubscribe = subscribeToWaybillPrint(
            packingStep.selectedShipment.shipmentId,
            (data) => handleShipmentMessageRef.current(data)
        )

        return () => {
            unsubscribe()
            clearTimeout(slipIsPrintedTimeout.current)
        }
    }, []);

    // The API sends one "printed" message per document (waybill, return waybill, return
    // form): only the first one moves the packing forward.
    function handlePrintValidated() {
        if (printValidated.current) {
            return
        }
        printValidated.current = true
        validPrint()
    }

    function handleShipmentMessage (data) {
        clearTimeout(slipIsPrintedTimeout.current)
        if (printValidated.current) {
            return
        }
        if (ShipmentMessageSeverity.ERROR === data.severity) {
            setIsLoading(false)
            setErrorMessage(data.message)
        }
        if (ShipmentMessageSeverity.SUCCESS === data.severity) {
            handlePrintValidated()
        }
    }

    function handleValid () {
        handlePrintDocuments()
        slipIsPrintedTimeout.current = setTimeout(() => {
            getShipmentById(packingStep.selectedShipment.shipmentId).then((response) => {
                if (response.documents && response.trackingNumber) {
                    handlePrintValidated()
                } else {
                    setIsLoading(false)
                    setErrorMessage(response.error ?? <FormattedMessage id={"wms.packing.modal.shipmentWeigh.defaultErrorMessage"}/>)
                }
            })
        }, 5000)
    }

    if (stopMultiExpSession) {
        return (
            <MultiExpForceStopModal open={stopMultiExpSession} handleClose={() => setStopMultiExpSession(false)}/>
        )
    }


    return (
        <Popup
            open={open}
            onClose={isMultiSessionPacking ? () => setStopMultiExpSession(true) : handleClose}
            icon={<PinIcon/>}
            title={<FormattedMessage id={"wms.packing.modal.click&collect.title"}/>}
            subtitle={<FormattedMessage id={"wms.packing.modal.click&collect.subtitle"}/>}
            strictOnclose
        >
            <Grid
                container
                direction={'column'}
                gap={'8px'}
            >
                {errorMessage && (
                    <Grid container direction={"column"} gap={"8px"}>
                        <Grid container direction={"column"}>
                            <Grid item><FormattedMessage id={"wms.packing.modal.shipmentWeigh.error"}/></Grid>
                            <Grid item sx={{color: red500}}>{errorMessage}</Grid>
                        </Grid>
                        <Grid container direction={"column"}>
                            <Grid item><FormattedMessage id={"wms.packing.modal.shipmentWeigh.toOMS"}/></Grid>
                            <Grid item>
                                <Link target="_blank" to={"../picking/orders/show/"+packingStep.selectedShipment.orderId}><FormattedMessage id={"wms.packing.modal.shipmentWeigh.toOMS.link"}/></Link>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <CustomButton
                                loading={isLoading}
                                disabled={isLoading}
                                onClick={handleValid}
                                fullWidth
                                color={"default"}
                                style={{marginTop: "8px"}}
                            >
                                {!isLoading ?
                                    <FormattedMessage id={"wms.packing.modal.scanDropZone.printButton"}/> :
                                    <FormattedMessage id={"wms.packing.modal.scanDropZone.printButton.isLoading"}/>
                                }

                            </CustomButton>
                        </Grid>
                    </Grid>
                )}

                {!errorMessage && (
                    <CustomButton
                        fullWidth
                        onClick={handleValid}
                        loading={isLoading}
                        disabled={isLoading}
                    >
                        <FormattedMessage id={"wms.packing.modal.click&collect.validButton"}/>
                    </CustomButton>
                )}

                {isMultiSessionPacking && (
                    <Grid
                        container
                        direction={"column"}
                        alignItems={'center'}
                        gap={"8px"}
                        sx={{marginTop: '8px'}}
                    >
                        <CustomButton
                            fullWidth
                            onClick={() => nextShipment()}
                            loading={isLoading}
                            disabled={isLoading}
                            color={'lightRed'}
                            variant={'nude'}
                        >
                            <FormattedMessage id={"wms.packing.item.multiExpSession.nextShipment"}/>
                        </CustomButton>

                        <Badge
                            size={"large"}
                            rounded
                            label={<FormattedMessage id={"wms.packing.item.multiSessionPacking.notice"}/>}
                            color={"online"}
                        />
                    </Grid>
                )}
            </Grid>
        </Popup>
    );
}

export default ClickAndCollectPackingModal;